import React, { useContext, useEffect } from 'react';
import { Box, Stack, Paper } from '@mui/material';
import { Table, TableBody, TableContainer, TableHead, TableRow, Typography, IconButton, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AccordionSummary, AccordionDetails, Accordion } from '../shared/components/accordion';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import ConfirmDialog from '../shared/components/confirmDialog';
import NurseVacationForm from './nurseVacationForm';
import { FormMode } from '../utils/extensions/constantEx'
import EditIcon from '@mui/icons-material/Edit';
import { HomeContext } from '../utils/contexts/homeContext';
import { NurseFormContext } from '../utils/contexts/nurseFormContext';
import { NurseVacationTrackerAPIUrl, GetDayJSValueFromUTCDate } from '../utils/extensions/functionEx';
import { DATE_DISPLAY_FORMAT } from '../startups/apiConfig';
import { useCookies } from 'react-cookie';

const NurseVacationList = (props) => {
    const { expanded, formOpenMode, nurseId, handleAccordionChange, handlePageLoadingOpen, handlePageLoadingClose } = props;
    const { ShowAPIError, OpenErrorAlert, homeState, OpenSuccessAlert, ClearLoggedUser } = useContext(HomeContext);
    const { nurseVacations, SetNurseVacations } = useContext(NurseFormContext);

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(0);
    const [isOpenEditForm, setIsOpenEditForm] = React.useState(false);
    const [selectedVacation, setSelectedVacation] = React.useState(null);
    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (nurseId && nurseId > 0) {
            fetchVacationData();
        }
    }, [nurseId]);

    async function fetchVacationData() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseVacationTrackerAPIUrl(homeState.loggedUser.companyCode, nurseId), {
                method: "GET",
                headers: headers
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                SetNurseVacations(await response.json());
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setIsDeleteConfirmOpen(true);
    }

    const handleDeleteOKClick = async () => {
        try {
            handlePageLoadingOpen();
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseVacationTrackerAPIUrl(homeState.loggedUser.companyCode), {
                method: "DELETE",
                headers: headers,
                body: JSON.stringify({ 'id': selectedId })
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Nurse vacation successfully deleted.');
                await fetchVacationData();
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            setIsDeleteConfirmOpen(false);
            setSelectedId(0);
            handlePageLoadingClose();
        }
    }

    const handleDeleteCancelClick = () => {
        setSelectedId(0);
        setIsDeleteConfirmOpen(false);
    }

    const handleEditClick = async (vacation) => {
        setSelectedVacation(vacation);
        setIsOpenEditForm(true);
    }

    const handleEditFormClose = () => {
        setSelectedVacation(null);
        setIsOpenEditForm(false);
    }

    const handleAddVacationClick = () => {
        setSelectedVacation(null);
        setIsOpenEditForm(true);
    }

    return (

        <Accordion expanded={expanded === 'panelNurseVacations'} onChange={handleAccordionChange('panelNurseVacations')}>
            <AccordionSummary id="panel-nurse-vacations">
                <Typography>Nurse Vacations</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Box sx={{ pr: 1, float: 'right' }}>
                                <Stack direction="row" spacing={2}>
                                    {
                                        isDeleteConfirmOpen &&
                                        <ConfirmDialog isOpen={isDeleteConfirmOpen} title="Confirm Delete"
                                            message="Are you sure you want to delete the vacation details?"
                                            handleClickOK={handleDeleteOKClick}
                                            handleClickCancel={handleDeleteCancelClick} />
                                    }
                                    {
                                        isOpenEditForm &&
                                        <NurseVacationForm isOpen={isOpenEditForm} nurseId={nurseId} handleClose={handleEditFormClose} selectedVacation={selectedVacation} />
                                    }
                                    <Button variant="contained" onClick={handleAddVacationClick}>
                                        Add Vacation
                                    </Button>
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid size={12}>
                            <TableContainer component={Paper} sx={{ maxHeight: '77vh' }}>
                                <Table stickyHeader sx={{ minWidth: 700 }}>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell sx={{ width: '250px' }}>Start Date</StyledTableCell>
                                            <StyledTableCell sx={{ width: '300px' }}>End Date</StyledTableCell>
                                            <StyledTableCell>Notes</StyledTableCell>
                                            <StyledTableCell sx={{ width: '100px' }}>&nbsp;</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {nurseVacations.map((row) => (
                                            <StyledTableRow key={row.id.toString()}>
                                                <StyledTableCell component="th" scope="row">
                                                    {GetDayJSValueFromUTCDate(row.start_date).format(DATE_DISPLAY_FORMAT)}
                                                </StyledTableCell>
                                                <StyledTableCell>{GetDayJSValueFromUTCDate(row.end_date).format(DATE_DISPLAY_FORMAT)}</StyledTableCell>
                                                <StyledTableCell>{row.remarks}</StyledTableCell>
                                                <StyledTableCell>

                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        sx={{
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        {
                                                            formOpenMode === FormMode.ViewMode &&
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <IconButton size="small" onClick={() => handleEditClick(row)}>
                                                                    <EditIcon fontSize="inherit" />
                                                                </IconButton>
                                                            </Stack>
                                                        }
                                                        {
                                                            formOpenMode === FormMode.ViewMode &&
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <IconButton size="small" color="error" onClick={() => handleDeleteClick(row.id)}>
                                                                    <RemoveCircleOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        }</Stack>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Stack>
            </AccordionDetails>
        </Accordion>

    );
}
export default NurseVacationList;